import { useTranslation as useTranslationBase, type UseTranslationResponse } from "react-i18next";

const XML_TAG_RE = /<\/?[a-zA-Z][\w-]*(?:\s[^>]*)?\/?>/;
const XML_TAG_RE_GLOBAL = /<\/?[a-zA-Z][\w-]*(?:\s[^>]*)?\/?>/g;

/**
 * Wrapper around react-i18next's useTranslation that strips XML component
 * tags from t() results. If a translation string contains tags like
 * `<strong>` or `<privacyLink>` but is rendered via t() instead of <Trans>,
 * the tags are stripped so users see clean text, and a console error is
 * logged so developers can fix the callsite.
 *
 * This does NOT affect <Trans>, which calls i18n.t() directly and handles
 * its own tag parsing.
 */
export function useTranslation(
  ...args: Parameters<typeof useTranslationBase>
): UseTranslationResponse<string, undefined> {
  const result = useTranslationBase(...args);

  try {
    const { t: originalT } = result;

    // SAFETY: wrappedT preserves originalT's call contract and copied properties.
    const wrappedT = ((...tArgs: Parameters<typeof originalT>) => {
      // SAFETY: react-i18next accepts a string key as the first t() argument.
      const value = originalT(...(tArgs as [string]));
      try {
        if (value.constructor === String && XML_TAG_RE.test(String(value))) {
          console.error(
            `[i18n] Translation key "${String(tArgs[0])}" contains XML component tags but was called with t() instead of <Trans>. The tags have been stripped. Use <Trans> with a components prop to render them.`,
          );
          return String(value).replace(XML_TAG_RE_GLOBAL, "");
        }
      } catch {
        // If XML stripping fails, return the original value as-is
      }
      return value;
    }) as typeof originalT;

    // Preserve properties like .ns that <Trans> and other utilities read
    Object.assign(wrappedT, originalT);

    return { ...result, t: wrappedT };
  } catch {
    // If wrapping fails entirely, return the unwrapped result
    return result;
  }
}
